import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth, type AuthUser } from '../AuthProvider';
import UserService from '../services/user.service';
import { normalizeAudioSettingLevel0to100 } from '../utils/applyAudioSettingsPatch';
import Confirm from './Confirm';
import { VolumeSlider } from './VolumeSlider';

import '../pages/profile.css';

export interface OwnedTitleOption {
    id: number;
    text: string;
}

export interface SettingsProps {
    user: AuthUser;
    /** Populated when item/title APIs exist; empty list shows a placeholder option */
    ownedTitles?: OwnedTitleOption[];
    /** When true, title list is still loading (select disabled until options arrive). */
    ownedTitlesLoading?: boolean;
}

/** Fields sent as `user` in `PUT /api/users/me`. */
export interface SettingsPendingChange {
    username?: string;
    music?: number;
    sfx?: number;
    titleId?: number | null;
}

const EMPTY_TITLES: OwnedTitleOption[] = [];

const deleteTitle = 'Are you sure?';
const deleteBody =
    'Are you sure you want to delete your account? All data associated with your account, including game data, purchases, and achievements will be lost. This action cannot be undone. Do you wish to proceed?';

function baselineMusicLevel(user: AuthUser): number {
    return normalizeAudioSettingLevel0to100(user.settings?.music);
}

function baselineSfxLevel(user: AuthUser): number {
    return normalizeAudioSettingLevel0to100(user.settings?.sfx);
}

function baselineTitleId(user: AuthUser): string {
    return user.settings?.titleId != null ? String(user.settings.titleId) : '';
}

const Settings: React.FC<SettingsProps> = ({ user, ownedTitles, ownedTitlesLoading = false }) => {
    const titles = ownedTitles ?? EMPTY_TITLES;
    const { logout, setLoggedInUser } = useAuth();
    const navigate = useNavigate();

    const [newUsername, setNewUsername] = useState('');
    const [newMusic, setNewMusic] = useState(() => baselineMusicLevel(user));
    const [newSfx, setNewSfx] = useState(() => baselineSfxLevel(user));
    const [selectedTitleId, setSelectedTitleId] = useState(() => baselineTitleId(user));

    const [confirmModal, setConfirmModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setNewUsername('');
        setNewMusic(baselineMusicLevel(user));
        setNewSfx(baselineSfxLevel(user));
        if (ownedTitlesLoading) {
            setSelectedTitleId('');
        } else if (titles.length === 0) {
            setSelectedTitleId('');
        } else {
            setSelectedTitleId(baselineTitleId(user));
        }
    }, [user.userId, user.settings, titles, ownedTitlesLoading]);

    const buildPendingChanges = useCallback((): SettingsPendingChange => {
        const data: SettingsPendingChange = {};
        if (newUsername.trim() !== '' && newUsername.trim() !== user.username) {
            data.username = newUsername.trim();
        }
        if (newMusic !== baselineMusicLevel(user)) {
            data.music = newMusic;
        }
        if (newSfx !== baselineSfxLevel(user)) {
            data.sfx = newSfx;
        }
        const baseTitle = baselineTitleId(user);
        if (selectedTitleId !== baseTitle) {
            if (selectedTitleId === '') {
                data.titleId = null;
            } else {
                const n = Number(selectedTitleId);
                if (!Number.isNaN(n)) {
                    data.titleId = n;
                }
            }
        }
        return data;
    }, [newMusic, newSfx, newUsername, selectedTitleId, user]);

    async function handleSaveChanges(event?: React.FormEvent | React.MouseEvent) {
        event?.preventDefault();
        const pending = buildPendingChanges();
        if (Object.keys(pending).length === 0) {
            return;
        }
        setIsSaving(true);
        try {
            const { data } = await UserService.update(pending);
            setLoggedInUser(data);
        } catch (e) {
            console.error(e);
            alert('Could not save settings. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }

    async function handleLogout() {
        await logout();
        navigate('/login');
    }

    function handleDeleteClick() {
        setConfirmModal(true);
    }

    async function handleConfirmDelete() {
        setConfirmModal(false);
        try {
            await UserService.delete();
            await logout();
            navigate('/login');
        } catch (e) {
            console.error(e);
            alert('Could not delete account at this time.');
        }
    }

    const emailDisplay = user.email ?? '';

    return (
        <div className="settings_container">
            <form className="settings_options" onSubmit={handleSaveChanges}>
                <div className="settings_option">
                    <label className="settings_label" htmlFor="settings-email">
                        Email:
                    </label>
                    <input
                        id="settings-email"
                        type="text"
                        placeholder={emailDisplay || '—'}
                        className="form_option"
                        readOnly
                        value={emailDisplay}
                    />
                </div>
                <div className="settings_option">
                    <label className="settings_label" htmlFor="settings-username">
                        Username:
                    </label>
                    <input
                        id="settings-username"
                        type="text"
                        placeholder={user.username}
                        value={newUsername}
                        className="form_option"
                        onChange={(e) => setNewUsername(e.target.value)}
                    />
                </div>
                <div className="settings_option">
                    <label className="settings_label" htmlFor="settings-music">
                        Music:
                    </label>
                    <VolumeSlider
                        id="settings-music"
                        value={newMusic}
                        onChange={setNewMusic}
                        disabled={isSaving}
                    />
                </div>
                <div className="settings_option">
                    <label className="settings_label" htmlFor="settings-sfx">
                        Sound Effects:
                    </label>
                    <VolumeSlider
                        id="settings-sfx"
                        value={newSfx}
                        onChange={setNewSfx}
                        disabled={isSaving}
                    />
                </div>
                <div className="settings_option">
                    <label className="settings_label" htmlFor="settings-title">
                        Title:
                    </label>
                    <select
                        id="settings-title"
                        value={selectedTitleId}
                        className="form_option"
                        disabled={ownedTitlesLoading}
                        onChange={(e) => setSelectedTitleId(e.target.value)}
                    >
                        {ownedTitlesLoading ? (
                            <option value="">Loading titles…</option>
                        ) : titles.length === 0 ? (
                            <option value="">No titles yet</option>
                        ) : (
                            titles.map((item) => (
                                <option key={item.id} value={String(item.id)}>
                                    {item.text}
                                </option>
                            ))
                        )}
                    </select>
                </div>
            </form>
            <button type="button" className="button_std" onClick={handleLogout}>
                Log Out
            </button>
            <button type="button" className="button_std" onClick={handleDeleteClick}>
                Delete Account
            </button>
            <div className="settings_bottom">
                <button
                    type="button"
                    className="button_std"
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
            </div>
            {confirmModal && (
                <Confirm
                    title={deleteTitle}
                    text={deleteBody}
                    onConfirm={handleConfirmDelete}
                    closeModal={setConfirmModal}
                />
            )}
        </div>
    );
};

export default Settings;
